import type { TaskGroupBy, TaskSort, TaskSortKey } from "@hq/contracts";
import {
  Button,
  DropdownMenu,
  IconButton,
  Popover,
  Select,
  Switch,
  ViewSwitcher,
  type ViewMode,
} from "@z/ui";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronDown,
  CircleCheck,
  Layers,
  SlidersHorizontal,
  Tag,
} from "lucide-react";
import type { TagCount } from "./tags";

export interface ViewOptionsProps {
  displayControls?: boolean;
  mode?: ViewMode;
  onModeChange?: (mode: ViewMode) => void;
  sort?: TaskSort;
  onSortChange?: (sort: TaskSort) => void;
  groupBy?: TaskGroupBy;
  onGroupByChange?: (groupBy: TaskGroupBy) => void;
  /** Omit to hide the toggle (e.g. the Completed view). */
  showCompleted?: boolean;
  onShowCompletedChange?: (show: boolean) => void;
  tags?: readonly string[];
  onTagsChange?: (tags: readonly string[]) => void;
  availableTags?: readonly TagCount[];
}

export const SORT_LABELS: Record<TaskSortKey, string> = {
  manual: "Manual",
  dueDate: "Deadline",
  scheduledAt: "Scheduled",
  priority: "Priority",
  createdAt: "Created",
  updatedAt: "Updated",
  completedAt: "Completed",
  title: "Title",
};
const SORT_KEYS = Object.keys(SORT_LABELS) as TaskSortKey[];
const GROUP_LABELS: Record<TaskGroupBy, string> = {
  none: "No grouping",
  project: "Project",
  dueDate: "Deadline",
  priority: "Priority",
  heading: "Heading",
  status: "Status",
  plannedDate: "When / deadline",
};
const GROUP_KEYS = Object.keys(GROUP_LABELS) as TaskGroupBy[];
const rowLabelClassName = "flex shrink-0 items-center gap-2 text-sm text-fg-muted [&_svg]:size-4";
const selectClassName = "w-auto max-w-44 border-transparent bg-transparent hover:bg-fg/6";

/** Display preferences stay open while the view updates behind the popover. */
export function ViewOptions({
  displayControls = true,
  mode,
  onModeChange,
  sort,
  onSortChange,
  groupBy,
  onGroupByChange,
  showCompleted,
  onShowCompletedChange,
  tags = [],
  onTagsChange,
  availableTags = [],
}: ViewOptionsProps) {
  // Keep active tags available even if no task carries them any more.
  const menuTags = [
    ...availableTags,
    ...tags
      .filter(
        (tag) => !availableTags.some((entry) => entry.tag.toLowerCase() === tag.toLowerCase()),
      )
      .map((tag) => ({ tag, count: 0 })),
  ];
  const hasTags = onTagsChange !== undefined && menuTags.length > 0;
  const hasLayout = mode !== undefined && onModeChange !== undefined;
  const hasCompleted =
    mode !== "board" && showCompleted !== undefined && onShowCompletedChange !== undefined;
  const hasSort = displayControls && sort !== undefined && onSortChange !== undefined;
  const hasGrouping = displayControls && groupBy !== undefined && onGroupByChange !== undefined;
  if (!hasLayout && !hasCompleted && !hasSort && !hasGrouping && !hasTags) return null;

  return (
    <Popover.Root>
      <Popover.Trigger
        render={
          <Button
            variant="ghost"
            size="sm"
            className="h-8 shrink-0 data-popup-open:bg-fg/6"
            aria-label="View options"
          >
            <SlidersHorizontal />
            View
            {tags.length > 0 && (
              <span className="rounded-sm bg-accent/10 px-1 text-[10px] text-accent tabular-nums">
                {tags.length}
              </span>
            )}
          </Button>
        }
      />
      <Popover.Content align="end" className="w-80 max-w-[calc(100vw-2rem)] p-4">
        <Popover.Title className="sr-only">View options</Popover.Title>
        <div className="flex flex-col gap-4">
          {(hasLayout || hasCompleted) && (
            <section className="flex flex-col gap-3" aria-label="Layout">
              {hasLayout && (
                <>
                  <h3 className="text-sm font-semibold">Layout</h3>
                  <ViewSwitcher value={mode} onChange={onModeChange} />
                </>
              )}
              {hasCompleted && (
                <label className="flex min-h-8 items-center justify-between gap-4 text-sm select-none">
                  <span className={rowLabelClassName}>
                    <CircleCheck aria-hidden="true" />
                    Show completed
                  </span>
                  <Switch checked={showCompleted} onCheckedChange={onShowCompletedChange} />
                </label>
              )}
            </section>
          )}
          {(hasSort || hasGrouping) && (
            <section
              className="flex flex-col gap-1 border-t border-border pt-3"
              aria-label="Display"
            >
              {hasSort && (
                <div className="flex min-h-9 items-center justify-between gap-2">
                  <span className={rowLabelClassName}>
                    <ArrowUpDown aria-hidden="true" />
                    Sort by
                  </span>
                  <div className="flex min-w-0 items-center gap-1">
                    <Select.Root
                      value={sort.by}
                      items={SORT_LABELS}
                      onValueChange={(by) => {
                        if (by !== null) onSortChange({ by, direction: sort.direction });
                      }}
                    >
                      <Select.Trigger aria-label="Sort by" className={selectClassName} />
                      <Select.Content>
                        {SORT_KEYS.map((key) => (
                          <Select.Item key={key} value={key}>
                            {SORT_LABELS[key]}
                          </Select.Item>
                        ))}
                      </Select.Content>
                    </Select.Root>
                    <IconButton
                      label={sort.direction === "asc" ? "Sort descending" : "Sort ascending"}
                      size="sm"
                      onClick={() =>
                        onSortChange({
                          by: sort.by,
                          direction: sort.direction === "asc" ? "desc" : "asc",
                        })
                      }
                    >
                      {sort.direction === "asc" ? <ArrowUp /> : <ArrowDown />}
                    </IconButton>
                  </div>
                </div>
              )}
              {hasGrouping && (
                <div className="flex min-h-9 items-center justify-between gap-2">
                  <span className={rowLabelClassName}>
                    <Layers aria-hidden="true" />
                    Group by
                  </span>
                  <Select.Root
                    value={groupBy}
                    items={GROUP_LABELS}
                    onValueChange={(value) => {
                      if (value !== null) onGroupByChange(value);
                    }}
                  >
                    <Select.Trigger aria-label="Group by" className={selectClassName} />
                    <Select.Content>
                      {GROUP_KEYS.map((key) => (
                        <Select.Item key={key} value={key}>
                          {GROUP_LABELS[key]}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Root>
                </div>
              )}
            </section>
          )}
          {hasTags && (
            <section
              className="flex flex-col gap-2 border-t border-border pt-3"
              aria-label="Filter by"
            >
              <h3 className="text-sm font-semibold">Filter by</h3>
              <div className="flex min-h-9 items-center justify-between gap-2">
                <span className={rowLabelClassName}>
                  <Tag aria-hidden="true" />
                  Tags
                </span>
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger
                    render={
                      <Button variant="ghost" className="h-8" aria-label="Filter by tag">
                        {tags.length === 0
                          ? "All"
                          : `${tags.length} tag${tags.length === 1 ? "" : "s"}`}
                        <ChevronDown className="text-fg-muted" />
                      </Button>
                    }
                  />
                  <DropdownMenu.Content align="end">
                    <DropdownMenu.Label>Filter by tag</DropdownMenu.Label>
                    {menuTags.map((entry) => {
                      const active = tags.some(
                        (tag) => tag.toLowerCase() === entry.tag.toLowerCase(),
                      );
                      return (
                        <DropdownMenu.CheckboxItem
                          key={entry.tag}
                          checked={active}
                          onCheckedChange={() =>
                            onTagsChange(
                              active
                                ? tags.filter(
                                    (tag) => tag.toLowerCase() !== entry.tag.toLowerCase(),
                                  )
                                : [...tags, entry.tag],
                            )
                          }
                        >
                          {entry.tag}
                          <span className="ml-auto pl-4 text-xs text-fg-faint">{entry.count}</span>
                        </DropdownMenu.CheckboxItem>
                      );
                    })}
                    {tags.length > 0 && (
                      <>
                        <DropdownMenu.Separator />
                        <DropdownMenu.Item onClick={() => onTagsChange([])}>
                          Clear tag filter
                        </DropdownMenu.Item>
                      </>
                    )}
                  </DropdownMenu.Content>
                </DropdownMenu.Root>
              </div>
            </section>
          )}
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}
